import apiClient from "@toothfairy/shared-api/apiClient";
import secureRequest from "@toothfairy/shared-api/secureAPI";
import envConfig from "../../../envConfig";
import AppAuth from "@toothfairy/shared-ui/AppAuthScreen";
import Prompts from "../Prompts";
import AgentFunctions from "../AgentFunctions";

const fetchHireableAgents = async () => {
  const url = `${
    envConfig.REACT_APP_ENV == "dev"
      ? "https://api.toothfairylab.link"
      : "https://api.toothfairyai.com"
  }/global_utils/fetch_hireable_agents`;

  const payload = await AppAuth.getCurrentSession();
  return secureRequest(
    apiClient.apiClient,
    "GET",
    url,
    null,
    null,
    true,
    null,
    payload,
  );
};

const fetchGlobalTools = async () => {
  const url = `${
    envConfig.REACT_APP_ENV == "dev"
      ? "https://api.toothfairylab.link"
      : "https://api.toothfairyai.com"
  }/global_utils/fetch_global_agent_functions`;

  const payload = await AppAuth.getCurrentSession();
  return secureRequest(
    apiClient.apiClient,
    "GET",
    url,
    null,
    null,
    true,
    null,
    payload,
  );
};

const fetchHireableToolsFromTemplates = async () => {
  try {
    const response = await fetchHireableAgents();
    const agents = response?.data?.items || [];

    const toolGroups = {};

    for (const agent of agents) {
      if (!agent?.agentHiringTemplate) continue;

      try {
        const templateStr = typeof agent.agentHiringTemplate === 'string' 
          ? agent.agentHiringTemplate 
          : JSON.stringify(agent.agentHiringTemplate);
        const template = JSON.parse(templateStr);
        const functions = template?.resources?.functions || [];

        if (functions.length > 0) {
          const groupName = template.toolsGroupName || `${agent.label} Tools`;

          // Include AuGov flag for filtering in components
          const isAuGov = template?.AuGov === "true";

          const toolsWithAuth = functions.map((fn) => ({
            ...fn,
            requiresAuth: fn?.requiresAuth === true || fn?.authType !== 'none' && fn?.authType !== undefined,
            isAuGov: isAuGov,
          }));

          if (!toolGroups[groupName]) {
            toolGroups[groupName] = {
              agentId: agent.id,
              fields: template.fields || [],
              tools: [],
              isAuGov: isAuGov,
            };
          }

          toolGroups[groupName].tools.push(...toolsWithAuth);
        }
      } catch (parseError) {
        console.warn(`Failed to parse template for agent ${agent.id}:`, parseError);
      }
    }

    return toolGroups;
  } catch (error) {
    console.error("Error fetching hireable tools from templates:", error);
    return {};
  }
};

const fetchHireablePromptsFromTemplates = async () => {
  try {
    const response = await fetchHireableAgents();
    const agents = response?.data?.items || [];

    const promptGroups = {};

    for (const agent of agents) {
      if (!agent?.agentHiringTemplate) continue;

      try {
        const templateStr = typeof agent.agentHiringTemplate === 'string' 
          ? agent.agentHiringTemplate 
          : JSON.stringify(agent.agentHiringTemplate);
        const template = JSON.parse(templateStr);
        const prompts = template?.resources?.prompts || [];

        if (prompts.length > 0) {
          const groupName = template.promptsGroupName || `${agent.label} Prompts`;

          // Include AuGov flag for filtering in components
          const isAuGov = template?.AuGov === "true";

          const promptsWithAuGov = prompts.map((p) => ({
            ...p,
            isAuGov: isAuGov,
          }));

          if (!promptGroups[groupName]) {
            promptGroups[groupName] = {
              agentId: agent.id,
              prompts: [],
              isAuGov: isAuGov,
            };
          }

          promptGroups[groupName].prompts.push(...promptsWithAuGov);
        }
      } catch (parseError) {
        console.warn(`Failed to parse template for agent ${agent.id}:`, parseError);
      }
    }

    return promptGroups;
  } catch (error) {
    console.error("Error fetching hireable prompts from templates:", error);
    return {};
  }
};

const fetchCompleteToolData = async (toolId) => {
  try {
    const result = await AgentFunctions.getAgentFunction(toolId);
    return result?.data?.getAgentFunction;
  } catch (error) {
    console.error(`Error fetching tool ${toolId}:`, error);
    return null;
  }
};

const fetchCompletePromptData = async (promptId) => {
  try {
    const result = await Prompts.getCustomPrompt(promptId);
    return result?.data?.getCustomPrompt;
  } catch (error) {
    console.error(`Error fetching prompt ${promptId}:`, error);
    return null;
  }
};

export default {
  fetchHireableToolsFromTemplates,
  fetchHireablePromptsFromTemplates,
  fetchCompleteToolData,
  fetchCompletePromptData,
};